import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { useCompanies } from './hooks';
import { useCurrentDomain } from "./context/DomainContext";
import { getAllIntakeSheets, assignIntakeSheets, deleteIntakeSheetMapping } from './services/masterDataService';

const ConfigureIntakeSheet = () => {
    const { domain } = useCurrentDomain();
    const [intakeSheets, setIntakeSheets] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [selectedSheetIds, setSelectedSheetIds] = useState([]);
    const [mappedSheets, setMappedSheets] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [sheetSearch, setSheetSearch] = useState('');
    const [addLoading, setAddLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [sheetsLoading, setSheetsLoading] = useState(false);

    const { companies, loading: companiesLoading } = useCompanies(domain);

    const BRAND_GREEN = '#0aa375';

    const selectStyles = {
        control: (base) => ({
            ...base,
            minHeight: '30px',
            fontSize: '12px',
            borderColor: '#dee2e6',
            boxShadow: 'none',
            '&:hover': { borderColor: BRAND_GREEN },
        }),
        indicatorsContainer: (base) => ({ ...base, padding: '0 2px' }),
        dropdownIndicator: (base) => ({ ...base, padding: '0 4px' }),
        clearIndicator: (base) => ({ ...base, padding: '0 4px' }),
        option: (base, state) => ({
            ...base,
            fontSize: '12px',
            backgroundColor: state.isSelected ? BRAND_GREEN : state.isFocused ? '#e6f7f2' : 'white',
            color: state.isSelected ? 'white' : '#333',
        }),
        singleValue: (base) => ({ ...base, fontSize: '12px' }),
        placeholder: (base) => ({ ...base, fontSize: '12px', color: '#adb5bd' }),
    };

    const Field = ({ label, required }) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={{ fontSize: '10px', fontWeight: 600, color: 'black', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                {label}
                {required && <span style={{ color: '#dc3545', marginLeft: '3px', fontSize: '14px', lineHeight: '1' }}>*</span>}
            </span>
        </div>
    );
    const Spinner = () => (
        <div className="text-center py-4">
            <div className="spinner-border spinner-border-sm" style={{ color: BRAND_GREEN }} role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
        </div>
    );

    useEffect(() => {
        if (!selectedCompany) {
            setIntakeSheets([]);
            setMappedSheets([]);
            setSelectedSheetIds([]);
            setSelectedRows([]);
            setSelectAll(false);
            return;
        }
        fetchSheets();
    }, [selectedCompany]);

    const fetchSheets = async () => {
        setSheetsLoading(true);
        try {
            const data = await getAllIntakeSheets(selectedCompany.value, domain);
            setIntakeSheets(data.filter(s => !s.isAssigned));
            setMappedSheets(data.filter(s => s.isAssigned));
            setSelectedSheetIds([]);
            setSelectedRows([]);
            setSelectAll(false);
        } catch (err) {
            toast.error('Failed to load intake sheets.');
        } finally {
            setSheetsLoading(false);
        }
    };

    const handleAddMapping = async () => {
        if (!selectedCompany || selectedSheetIds.length === 0) return;
        setAddLoading(true);
        const loadingToast = toast.loading('Assigning intake sheets...');
        try {
            await assignIntakeSheets(selectedCompany.value, selectedSheetIds, domain);
            toast.dismiss(loadingToast);
            toast.success(`${selectedSheetIds.length} sheet(s) assigned successfully.`);
            await fetchSheets();
        } catch (err) {
            toast.dismiss(loadingToast);
            toast.error(err.response?.data?.message || 'Failed to assign intake sheets.');
        } finally {
            setAddLoading(false);
        }
    };

    const handleDeleteMapping = async () => {
        if (!selectedCompany || selectedRows.length === 0) return;
        const toastId = toast(
            <div style={{ textAlign: 'center', minWidth: '250px' }}>
                <div style={{ fontWeight: '600', marginBottom: '10px' }}>
                    Delete {selectedRows.length} mapping(s)?
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '10px' }}>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => toast.dismiss(toastId)}>
                        Cancel
                    </button>
                    <button className="btn btn-sm btn-danger" onClick={() => { toast.dismiss(toastId); performDelete(); }}>
                        Yes, Delete
                    </button>
                </div>
            </div>,
            { autoClose: false, closeButton: false, closeOnClick: false, draggable: false, className: 'bg-white shadow' }
        );
    };

    const performDelete = async () => {
        setDeleteLoading(true);
        const loadingToast = toast.loading('Deleting mappings...');
        try {
            await deleteIntakeSheetMapping(selectedCompany.value, selectedRows, domain);
            toast.dismiss(loadingToast);
            toast.success(`${selectedRows.length} mapping(s) deleted successfully.`);
            await fetchSheets();
        } catch (err) {
            toast.dismiss(loadingToast);
            toast.error(err.response?.data?.message || 'Failed to delete mappings.');
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleCompanyChange = (selected) => {
        setSelectedCompany(selected);
        setSelectedSheetIds([]);
        setSelectedRows([]);
        setSelectAll(false);
        setSheetSearch('');
    };

    const filteredSheets = intakeSheets.filter(s =>
        (s.szFormName || '').toLowerCase().includes(sheetSearch.toLowerCase())
    );

    const allMappedSelected = mappedSheets.length > 0 && mappedSheets.every(s => selectedRows.includes(s.id));

    const handleSelectAll = () => {
        if (allMappedSelected) setSelectedRows([]);
        else setSelectedRows(mappedSheets.map(s => s.id));
        setSelectAll(!allMappedSelected);
    };

    return (
        <div className="container-fluid p-0 px-1">
            <div className="card-body">
                <div className="row g-2">
                    <div className="col-12 col-lg-6">
                        <div className="card border h-100" style={{ borderRadius: '6px' }}>
                                <div className="card-header" style={{ color: '#fff', fontWeight: 600,fontSize: 14  , padding: '8px', backgroundColor: BRAND_GREEN }}>
                                    Add Intake Sheets
                            </div>
                            <div className="card-body p-2">
                                <div className="mb-2">
                                    <Field label="Company" required />
                                    <Select
                                        options={companies}
                                        value={selectedCompany}
                                        onChange={handleCompanyChange}
                                        isLoading={companiesLoading}
                                        placeholder="Select company..."
                                        isSearchable isClearable
                                        styles={selectStyles}
                                    />
                                </div>
                                <div className="mb-2">
                                    <Field label="Search Sheets" />
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="Filter sheet list..."
                                        value={sheetSearch}
                                        onChange={e => setSheetSearch(e.target.value)}
                                        disabled={!selectedCompany}
                                        style={{ fontSize: '12px' }}
                                    />
                                </div>
                                <div style={{ height: '280px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', background: '#fafafa' }}>
                                    {sheetsLoading ? (
                                        <Spinner />
                                    ) : filteredSheets.length === 0 ? (
                                        <div className="text-center py-4" style={{ color: '#adb5bd', fontSize: '12px' }}>
                                            {selectedCompany ? 'No unassigned sheets.' : 'Select a company first.'}
                                        </div>
                                    ) : (
                                        filteredSheets.map(s => (
                                            <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', fontSize: '12px' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedSheetIds.includes(s.id)}
                                                    onChange={() => setSelectedSheetIds(prev =>
                                                        prev.includes(s.id) ? prev.filter(id => id !== s.id) : [...prev, s.id]
                                                    )}
                                                    style={{ accentColor: BRAND_GREEN }}
                                                />
                                                {s.szFormName}
                                            </label>
                                        ))
                                    )}
                                </div>
                                <div className="d-flex justify-content-center gap-2 mt-2">
                                    <button
                                        onClick={() => { setSelectedSheetIds([]); setSheetSearch(''); }}
                                        className="btn btn-outline-secondary btn-sm px-3"
                                        style={{ fontSize: '12px' }}
                                    >
                                        Reset
                                    </button>
                                    <button
                                        onClick={handleAddMapping}
                                        disabled={addLoading || !selectedCompany || selectedSheetIds.length === 0}
                                        className="btn btn-sm text-white px-3 fw-semibold"
                                        style={{ backgroundColor: BRAND_GREEN, border: 'none', fontSize: '12px' }}
                                    >
                                        {addLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                                                Saving...
                                            </>
                                        ) : (
                                            `Add Mapping (${selectedSheetIds.length})`
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-lg-6">
                        <div className="card border h-100" style={{ borderRadius: '6px' }}>
                            <div className="card-header" style={{ color: '#fff', fontWeight: 600,fontSize: 14, padding: '8px', backgroundColor: BRAND_GREEN }}>
                                Mapped Intake Sheets
                            </div>
                            <div className="card-body p-2">
                                {mappedSheets.length > 0 && (
                                    <div className="d-flex align-items-center justify-content-between mb-1 px-1 py-1"
                                        style={{ borderBottom: '1px solid #e9ecef', minHeight: '28px' }}>

                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="checkbox"
                                                id="selectAllMapped"
                                                checked={allMappedSelected}
                                                onChange={handleSelectAll}
                                                disabled={mappedSheets.length === 0}
                                                style={{ accentColor: BRAND_GREEN, width: '14px', height: '14px', cursor: 'pointer' }}
                                            />
                                            <label className="form-check-label mb-0" style={{ fontSize: '11px', fontWeight: 600, color: '#495057', cursor: 'pointer', userSelect: 'none' }} htmlFor="selectAllMapped">
                                                Select All
                                            </label>
                                            {mappedSheets.length > 0 && (
                                                <span style={{ fontSize: '10px', color: '#adb5bd' }}>
                                                    ({mappedSheets.length} total)
                                                </span>
                                            )}
                                        </div>

                                        {selectedRows.length > 0 && (
                                            <span style={{
                                                fontSize: '10px',
                                                fontWeight: 600,
                                                background: '#e6f7f2',
                                                color: BRAND_GREEN,
                                                padding: '2px 8px',
                                                borderRadius: '20px',
                                                border: `1px solid ${BRAND_GREEN}22`
                                            }}>
                                                ✓ {selectedRows.length} selected
                                            </span>
                                        )}
                                    </div>
                                )}
                                <div style={{ height: '300px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                                    {sheetsLoading ? (
                                        <Spinner />
                                    ) : mappedSheets.length === 0 ? (
                                        <div className="alert alert-success small py-1 mb-0 m-2">No Active Mappings.</div>
                                    ) : (
                                        <table className="table table-sm table-hover mb-0" style={{ fontSize: '12px' }}>
                                            <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>
                                                <tr>
                                                    <th style={{ width: '36px', padding: '6px 10px' }}></th>
                                                    <th style={{ padding: '6px 10px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.3px', color: '#6c757d' }}>Sheet Name</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {mappedSheets.map(item => (
                                                    <tr key={item.id} style={{ background: selectedRows.includes(item.id) ? '#e6f7f2' : 'white' }}>
                                                        <td style={{ padding: '6px 10px' }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedRows.includes(item.id)}
                                                                onChange={() => setSelectedRows(prev =>
                                                                    prev.includes(item.id) ? prev.filter(id => id !== item.id) : [...prev, item.id]
                                                                )}
                                                                style={{ accentColor: BRAND_GREEN }}
                                                            />
                                                        </td>
                                                        <td style={{ padding: '6px 10px' }}>{item.szFormName}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                                <div className="d-flex justify-content-center mt-2">
                                    <button
                                        onClick={handleDeleteMapping}
                                        disabled={deleteLoading || selectedRows.length === 0}
                                        className="btn btn-sm btn-danger px-3"
                                        style={{ fontSize: '12px' }}
                                    >
                                        {deleteLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                                                Deleting...
                                            </>
                                        ) : (
                                            `Delete Selected (${selectedRows.length})`
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConfigureIntakeSheet;